import React from 'react'

const escapeCSV = (val) => {
  if (val === null || val === undefined) return ''
  const str = String(val)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

// Safely render any plan field value as text. Objects/arrays and nullish values
// never crash the cell — they render a dash. This is the core guardrail: no
// field value can throw during render.
const cell = (val) => {
  if (val === null || val === undefined || val === '') return '-'
  if (typeof val === 'object') return '-'
  return String(val)
}

// Render a star rating safely. The API's Rating.value is a double 0-4.5 and is
// -1 when unavailable. String.prototype.repeat throws on negative/NaN/huge
// counts, which previously crashed the whole page — so clamp to a valid integer
// count and fall back to the numeric value (or a dash) otherwise.
const renderRating = (rating) => {
  const value = rating && typeof rating.value === 'number' ? rating.value : null
  if (value === null || Number.isNaN(value) || value < 0) return '-'
  const stars = Math.max(0, Math.min(5, Math.floor(value)))
  const suffix = value % 1 >= 0.5 ? '½' : ''
  return stars === 0 && !suffix ? '-' : '★'.repeat(stars) + suffix
}

const getProviderStatus = (plan, provider) => {
  const id = provider && provider.id
  if (id === null || id === undefined) return 'Unknown'
  // Provider ids may be number or string across schemas while the plan's
  // in/out network arrays are numbers; compare by normalized string so a
  // type mismatch doesn't wrongly report "Unknown".
  const key = String(id)
  const inIds = Array.isArray(plan.inNetworkIds) ? plan.inNetworkIds.map(String) : []
  const outIds = Array.isArray(plan.outNetworkIds) ? plan.outNetworkIds.map(String) : []
  if (inIds.includes(key)) return 'In Network'
  if (outIds.includes(key)) return 'Out of Network'
  return 'Unknown'
}

const statusStyle = (status) => {
  if (status === 'In Network') return 'text-green-600'
  if (status === 'Out of Network') return 'text-red-600'
  return 'text-gray-400'
}

// Column definitions aligned to the OpenAPI PredictPlan / Plan (basic) schemas.
// `key` is either a field name (looked up on the plan) or a function of the
// plan. `align` controls cell alignment. Fields absent on basic plans simply
// render '-' via the safe `cell` helper.
const BASE_COLUMNS = [
  { header: 'Plan Name', key: 'planName' },
  { header: 'Carrier', key: 'carrier' },
  { header: 'Type', key: 'planType' },
  { header: 'Network', key: 'networkType' },
  { header: 'Bid ID', key: 'bidId' },
  { header: 'Mo. Premium', key: 'monthlyPremium', align: 'right' },
  { header: 'Annual Premium', key: 'premium', align: 'right' },
  { header: 'Est. Medical', key: 'estimatedMedicalCosts', align: 'right' },
  { header: 'Est. Pharmacy', key: 'estimatedPharmacyCosts', align: 'right' },
  { header: 'Supplemental Value', key: 'supplementalValue', align: 'right' },
  { header: 'Total Est.', key: 'totalEstimatedCosts', align: 'right' },
  { header: 'Net Impact', key: 'netImpact', align: 'right' },
  { header: 'Rating', key: (p) => renderRating(p.rating), align: 'center' }
]

const valueFor = (column, plan) =>
  typeof column.key === 'function' ? column.key(plan) : cell(plan[column.key])

const PlansModal = ({ plans, total, providers = [], onClose }) => {
  const planList = Array.isArray(plans) ? plans : []
  const providerList = Array.isArray(providers) ? providers : []
  const hasProviders = providerList.length > 0

  const columns = [...BASE_COLUMNS]
  if (hasProviders) {
    providerList.forEach((p) => {
      const name = p.presentation_name || p.presentationName || `Provider ${p.id}`
      columns.push({ header: name, key: (plan) => getProviderStatus(plan, p), align: 'center', provider: true })
    })
  }

  const exportCSV = () => {
    try {
      const headers = columns.map((c) => c.header)
      const rows = planList.map((plan) =>
        columns.map((c) => escapeCSV(valueFor(c, plan)))
      )
      const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `plans_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export CSV:', err)
    }
  }

  const alignClass = (align) =>
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50' onClick={onClose}>
      <div className='bg-white rounded-lg shadow-xl w-11/12 max-h-[85vh] flex flex-col' onClick={(e) => e.stopPropagation()}>
        <div className='flex justify-between items-center p-3 border-b'>
          <h2 className='text-lg font-semibold'>Plans ({total || planList.length})</h2>
          <div className='flex items-center gap-2'>
            <button onClick={exportCSV} className='text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded hover:bg-gray-100'>
              Export CSV
            </button>
            <button onClick={onClose} className='text-gray-500 hover:text-gray-700 text-2xl leading-none'>&times;</button>
          </div>
        </div>
        <div className='overflow-auto p-2'>
          <table className='w-full text-xs border-collapse'>
            <thead className='bg-gray-100 sticky top-0'>
              <tr>
                {columns.map((c, i) => (
                  <th key={i} className={`p-1 border-b ${alignClass(c.align)}`}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {planList.map((plan, idx) => {
                const safePlan = plan && typeof plan === 'object' ? plan : {}
                return (
                  <tr key={safePlan.bidId || idx} className='border-b hover:bg-gray-50'>
                    {columns.map((c, i) => {
                      const content = valueFor(c, safePlan)
                      const extra = c.provider ? statusStyle(content) : (c.header === 'Total Est.' ? 'font-semibold' : '')
                      return (
                        <td key={i} className={`p-1 ${alignClass(c.align)} ${extra}`}>{content}</td>
                      )
                    })}
                  </tr>
                )
              })}
              {planList.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className='p-3 text-center text-gray-500'>No plans to display.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default PlansModal
export { renderRating, cell, getProviderStatus, escapeCSV }
